import { useState, useEffect } from 'react'
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../../contexts/AuthContext'
import {
  LayoutDashboard,
  GraduationCap,
  Star,
  MessageSquare,
  Bell,
  Settings,
  LogOut,
  Menu,
  X,
  FileText,
  CreditCard,
  TrendingUp,
  Search,
  Heart
} from 'lucide-react'
import supabase from '../../services/supabaseClient'
import LanguageSelector from '../../components/LanguageSelector'

export default function ParentDashboard() {
  const { t } = useTranslation()
  const { profile, signOut } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [myChildren, setMyChildren] = useState([])
  const [stats, setStats] = useState({
    totalPoints: 0,
    unreadMessages: 0,
    unreadAnnouncements: 0
  })

  useEffect(() => {
    if (profile?.id) {
      fetchParentData()
    }
  }, [profile])

  async function fetchParentData() {
    try {
      const { data: relationships, error: relError } = await supabase
        .from('parent_child_relationships')
        .select('learner_id')
        .eq('parent_id', profile.id)

      if (relError) throw relError

      const learnerIds = relationships?.map(r => r.learner_id) || []

      if (learnerIds.length > 0) {
        const { data: children, error: childrenError } = await supabase
          .from('learners')
          .select('*')
          .in('id', learnerIds)

        if (childrenError) throw childrenError
        setMyChildren(children || [])

        const { data: points } = await supabase
          .from('points')
          .select('points')
          .in('learner_id', learnerIds)

        const totalPoints = points?.reduce((sum, p) => sum + p.points, 0) || 0

        const { count: messagesCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('recipient_id', profile.id)
          .eq('is_read', false)

        setStats({
          totalPoints,
          unreadMessages: messagesCount || 0,
          unreadAnnouncements: 0
        })
      }
    } catch (error) {
      console.error('Error fetching parent data:', error)
    }
  }

  const navigation = [
    { name: t('common.dashboard'), href: '/parent', icon: LayoutDashboard },
    { name: t('demo.myChildren'), href: '/parent/children', icon: GraduationCap },
    { name: t('dashboard.studentProgress'), href: '/parent/progress', icon: TrendingUp },
    { name: t('dashboard.portfolio'), href: '/parent/portfolio', icon: FileText },
    { name: t('common.messages'), href: '/parent/messages', icon: MessageSquare },
    { name: t('common.announcements'), href: '/parent/announcements', icon: Bell },
    { name: t('dashboard.billing'), href: '/parent/payments', icon: CreditCard },
    { name: t('common.settings'), href: '/parent/settings', icon: Settings },
  ]

  const isActive = (href) => location.pathname === href

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`}>
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-72 flex-col bg-white border-r border-gray-100">
          <div className="flex h-20 items-center justify-between px-6 border-b border-gray-100">
            <div className="flex items-center">
              <div className="h-10 w-10 rounded-xl bg-orange-500 flex items-center justify-center text-white font-bold shadow-lg shadow-orange-500/30">
                P
              </div>
              <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.parent')} {t('navigation.portal')}</span>
            </div>
            <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-gray-100 text-text-secondary">
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-72 lg:flex-col">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-100">
          <div className="flex h-20 items-center px-6 border-b border-gray-100">
            <div className="h-10 w-10 rounded-xl bg-orange-500 flex items-center justify-center text-white font-bold shadow-lg shadow-orange-500/30">
              P
            </div>
            <span className="ml-3 text-lg font-bold text-text-primary">{t('roles.parent')} {t('navigation.portal')}</span>
          </div>
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`nav-link ${isActive(item.href) ? 'active' : ''}`}
              >
                <item.icon className={`mr-3 h-5 w-5 ${isActive(item.href) ? 'text-primary-500' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            ))}
          </nav>
          <div className="p-4 border-t border-gray-100">
            <button
              onClick={signOut}
              className="flex items-center w-full px-4 py-3 text-sm font-medium text-text-secondary rounded-xl hover:bg-gray-100 hover:text-text-primary transition-colors"
            >
              <LogOut className="mr-3 h-5 w-5 text-gray-400" />
              {t('auth.signOut')}
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Top header */}
        <header className="dashboard-header sticky top-0 z-30">
          <div className="flex h-20 items-center justify-between px-4 sm:px-6 lg:px-8">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-text-secondary hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </button>
            
            {/* Search */}
            <div className="hidden md:flex flex-1 max-w-md ml-8">
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('common.search')}
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white border border-gray-200 text-text-primary placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-4 focus:ring-primary-100 transition-all"
                />
              </div>
            </div>
            
            <div className="flex items-center ml-auto space-x-4">
              <LanguageSelector size="small" />
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors relative">
                <Bell className="h-5 w-5" />
                {stats.unreadAnnouncements > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-primary-500 rounded-full text-xs text-white flex items-center justify-center border-2 border-white">
                    {stats.unreadAnnouncements}
                  </span>
                )}
              </button>
              <button className="p-3 rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-100 transition-colors relative">
                <MessageSquare className="h-5 w-5" />
                {stats.unreadMessages > 0 && (
                  <span className="absolute top-1.5 right-1.5 h-5 w-5 bg-primary-500 rounded-full text-xs text-white flex items-center justify-center border-2 border-white">
                    {stats.unreadMessages}
                  </span>
                )}
              </button>
              <div className="flex items-center pl-4 border-l border-gray-100">
                <div className="h-10 w-10 rounded-xl bg-orange-500 flex items-center justify-center text-white font-semibold shadow-lg shadow-orange-500/30">
                  {profile?.first_name?.[0]}{profile?.last_name?.[0]}
                </div>
                <div className="ml-3 hidden sm:block">
                  <p className="text-sm font-semibold text-text-primary">{profile?.first_name} {profile?.last_name}</p>
                  <p className="text-xs text-text-secondary">{t('roles.parent')}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="py-8 px-4 sm:px-6 lg:px-8">
          <Routes>
            <Route
              path="/"
              element={
                <div className="space-y-8 animate-fade-in">
                  {/* Page Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                      <h1 className="text-2xl font-bold text-text-primary font-heading">{t('roles.parent')} {t('common.dashboard')}</h1>
                      <p className="mt-1 text-sm text-text-secondary">
                        {t('dashboard.welcome', { name: profile?.first_name })}
                      </p>
                    </div>
                  </div>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon primary">
                          <GraduationCap className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('demo.myChildren')}</p>
                          <p className="text-2xl font-bold text-text-primary">{myChildren.length}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon success">
                          <Star className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.totalPoints')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.totalPoints}</p>
                        </div>
                      </div>
                    </div>

                    <div className="stat-card hover-lift">
                      <div className="flex items-center">
                        <div className="stat-card-icon warning">
                          <MessageSquare className="h-6 w-6" />
                        </div>
                        <div className="ml-4">
                          <p className="text-sm font-medium text-text-secondary">{t('dashboard.unreadMessages')}</p>
                          <p className="text-2xl font-bold text-text-primary">{stats.unreadMessages}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* My Children */}
                  <div className="card p-6">
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-lg font-bold text-text-primary font-heading">{t('demo.myChildren')}</h2>
                      <Link to="/parent/children" className="text-sm font-semibold text-primary-600 hover:text-primary-700">
                        {t('dashboard.viewAll')}
                      </Link>
                    </div>
                    {myChildren.length === 0 ? (
                      <div className="text-center py-12 text-text-secondary">
                        <GraduationCap className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                        <p className="font-medium">{t('demo.noChildren')}</p>
                        <p className="text-sm mt-1">{t('demo.contactSchool')}</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {myChildren.map((child) => (
                          <Link
                            key={child.id}
                            to={`/parent/children/${child.id}`}
                            className="flex items-center p-5 border border-gray-200 rounded-xl hover:border-primary-300 hover:shadow-soft transition-all group"
                          >
                            <div className="h-12 w-12 rounded-xl bg-card-teal flex items-center justify-center text-primary-600 font-bold text-lg group-hover:bg-primary-100 transition-all">
                              {child.first_name[0]}{child.last_name[0]}
                            </div>
                            <div className="ml-4 flex-1">
                              <h3 className="font-semibold text-text-primary group-hover:text-primary-600 transition-colors">{child.first_name} {child.last_name}</h3>
                              <p className="text-sm text-text-secondary">{t('common.grade')} {child.grade_level}</p>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center text-primary-600">
                                <Heart className="h-4 w-4 mr-1" />
                                <span className="font-semibold text-sm">{t('common.view')}</span>
                              </div>
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Quick Actions */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.quickActions')}</h2>
                      <div className="grid grid-cols-2 gap-4">
                        <Link
                          to="/parent/progress"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <TrendingUp className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.view')} {t('dashboard.studentProgress')}</span>
                        </Link>
                        <Link
                          to="/parent/portfolio"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <FileText className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('dashboard.portfolio')}</span>
                        </Link>
                        <Link
                          to="/parent/messages"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <MessageSquare className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.message')} {t('roles.teacher')}</span>
                        </Link>
                        <Link
                          to="/parent/payments"
                          className="flex items-center p-4 border border-gray-200 rounded-xl hover:border-primary-300 hover:bg-card-teal transition-all group"
                        >
                          <div className="p-2 rounded-lg bg-card-teal text-primary-500 group-hover:bg-primary-100 transition-colors">
                            <CreditCard className="h-5 w-5" />
                          </div>
                          <span className="ml-3 text-sm font-semibold text-text-secondary">{t('common.make')} {t('common.payment')}</span>
                        </Link>
                      </div>
                    </div>

                    <div className="card p-6">
                      <h2 className="text-lg font-bold text-text-primary mb-6 font-heading">{t('dashboard.recentAnnouncements')}</h2>
                      <div className="space-y-4">
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-text-primary">{t('demo.schoolEvent')}</span>
                            <span className="text-xs text-text-secondary">2 {t('common.days')} {t('common.ago')}</span>
                          </div>
                          <p className="text-sm text-text-secondary">{t('demo.sportsDay')}</p>
                        </div>
                        <div className="p-4 bg-gray-50 rounded-xl">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-semibold text-text-primary">{t('demo.importantNotice')}</span>
                            <span className="text-xs text-text-secondary">5 {t('common.days')} {t('common.ago')}</span>
                          </div>
                          <p className="text-sm text-text-secondary">{t('demo.feesDue')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              }
            />
            <Route path="*" element={<Navigate to="/parent" replace />} />
          </Routes>
        </main>
      </div>
    </div>
  )
}
